import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from '@reach/router';
import { extractLanguage } from '../helpers/common';

// Use useLayoutEffect on client (fires before paint) and fall back to useEffect on SSR
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function CookieNotice({
  children,
  backgroundClasses = 'container',
  backgroundWrapperClasses = 'bg-cookie fixed-bottom py-5',
  acceptButtonText = 'Accept',
  acceptButtonClasses = 'btn btn-primary btn-lg mx-2',
  buttonWrapperClasses = 'float-end',
  declineButton = true,
  declineButtonClasses = 'btn btn-secondary btn-lg mx-2',
  declineButtonText = 'Decline',
  wrapperClasses = 'd-flex justify-content-between cookie-notice',
  personalizeButtonClasses = 'btn btn-secondary',
  personalizeButtonText = 'Personalize',
  cookies = [{
    name: 'necessary',
    checked: true,
    editable: false,
    default: true,
    title: 'Essentiel',
    text: 'Essential cookie are necessary for the proper function of the website',
  }],
  cookieDays = 365,
  personalizeButtonEnable = true,
  cookieListClasses = 'row cookie-list',
  cookieMapClasses = 'col-12 col-md-6',
  cookieTitleClasses = '',
  personalizeValidationClasses = 'btn btn-primary btn-lg float-end',
  personalizeValidationWrapperClasses,
  personalizeValidationText = 'Save',
  imprintText,
  privacyText,
  imprintClasses = 'text-latori-green text-xs py-[10px] font-monospace uppercase mr-4 cursor-pointer',
  privacyClasses = 'text-latori-green text-xs py-[10px] font-monospace uppercase cursor-pointer',
  linkWrapperClasses = 'flex flex-wrap justify-center mx-auto my-4 text-center',
  altLangTexts,
  altLangLabel,
}) {
  const readCookieValue = (name) => {
    if (typeof document === 'undefined') return null;

    const encodedName = encodeURIComponent(name);
    const cookieEntry = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${encodedName}=`));

    if (!cookieEntry) return null;

    const rawValue = cookieEntry.slice(encodedName.length + 1);
    return decodeURIComponent(rawValue);
  };

  const [displayCookieList, setDisplayCookieList] = useState(false);
  const [displayCookieNotice, setDisplayCookieNotice] = useState(false);
  const [useAltLang, setUseAltLang] = useState(false);
  const dialogRef = useRef(null);
  const location = useLocation();

  const language = extractLanguage(location.pathname);

  const styles = {
    background: backgroundClasses,
    backgroundWrapper: backgroundWrapperClasses,
    acceptBtn: acceptButtonClasses,
    buttonWrapper: buttonWrapperClasses,
    declineBtn: declineButtonClasses,
    wrapper: wrapperClasses,
    personalizeButton: personalizeButtonClasses,
    cookiesList: cookieListClasses,
    cookieMap: cookieMapClasses,
    cookieTitle: cookieTitleClasses,
    personalizeValidation: personalizeValidationClasses,
    personalizeValidationWrapper: personalizeValidationWrapperClasses,
    imprintLink: imprintClasses,
    privacyLink: privacyClasses,
    linkWrapper: linkWrapperClasses,
  };

  const text = {
    acceptButton: acceptButtonText,
    declineButton: declineButtonText,
    personalizeButton: personalizeButtonText,
    personalizeValidation: personalizeValidationText,
  };

  const prop = {
    declineButtonActivated: declineButton,
    cookiesList: cookies,
    cookieDays,
    personalizeButton: personalizeButtonEnable,
  };

  const date = new Date();
  date.setTime(date.getTime() + prop.cookieDays * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  const secureAttribute = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? '; Secure'
    : '';

  const buildCookieAttributes = () => `${expires}; path=/; SameSite=Lax${secureAttribute}`;

  const setConsentStorage = (name, value) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(name, `${value}`);
    } catch (err) {
      // Ignore storage write failures (private mode, storage disabled).
    }
  };

  const getConsentStorage = (name) => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(name);
    } catch (err) {
      return null;
    }
  };

  const setCookie = (name, value) => {
    document.cookie = `${name}=${value}${buildCookieAttributes()}`;
    setConsentStorage(name, value);
  };

  const getConsentValue = (name) => {
    const cookieValue = readCookieValue(name);
    if (cookieValue !== null) return cookieValue;
    return getConsentStorage(name);
  };

  const dispatchCookieEvent = () => {
    if (typeof document !== 'undefined') {
      const event = new Event('cookies:updated');
      document.documentElement.dispatchEvent(event);
    }
  };

  const acceptCookie = () => {
    prop.cookiesList.forEach((c) => {
      setCookie(c.name, 'true');
    });
    setCookie('cookieConsent', 'accepted');

    setDisplayCookieNotice(false);
    dispatchCookieEvent();
  };

  const declineCookie = () => {
    prop.cookiesList.forEach((c) => {
      if (c.editable) {
        setCookie(c.name, 'false');
      } else {
        setCookie(c.name, c.default);
      }
    });
    setCookie('cookieConsent', 'declined');

    setDisplayCookieNotice(false);
    dispatchCookieEvent();
  };

  const validatePreferences = (e) => {
    e.preventDefault();

    prop.cookiesList.forEach((c) => {
      const checkbox = document.getElementById(c.name);
      if (checkbox && checkbox.checked) {
        setCookie(c.name, 'true');
      } else {
        setCookie(c.name, 'false');
      }
    });
    setCookie('cookieConsent', 'customized');

    setDisplayCookieNotice(false);
    setDisplayCookieList(false);
    setTimeout(() => {
      dispatchCookieEvent();
    }, 50);
  };

  // Check cookie state synchronously before first paint to avoid LCP element render delay.
  // useLayoutEffect fires after React renders but before the browser paints, so the banner
  // appears in the same frame as the rest of the page content (no visible delay).
  useIsomorphicLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const cookieConsent = getConsentValue('cookieConsent');
    if (cookieConsent) {
      setDisplayCookieNotice(false);
      return;
    }

    const allCookiesSet = prop.cookiesList.every((c) => {
      const val = getConsentValue(c.name);
      return val !== null && (val === 'true' || val === 'false');
    });

    setDisplayCookieNotice(!allCookiesSet);
  }, [prop.cookiesList]);

  // Auto-focus the dialog and trap focus within it
  useEffect(() => {
    if (!displayCookieNotice || !dialogRef.current) return;

    const dialog = dialogRef.current;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Focus the dialog itself on mount
    dialog.focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = dialog.querySelectorAll(focusableSelector);
      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl || document.activeElement === dialog) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [displayCookieNotice, displayCookieList]);

  // Resolve displayed texts: use alt language texts if toggled
  const displayedTexts = useAltLang && altLangTexts ? altLangTexts : null;
  const currentImprintText = displayedTexts?.imprintText || imprintText;
  const currentPrivacyText = displayedTexts?.privacyText || privacyText;
  const currentAcceptButtonText = displayedTexts?.acceptButtonText || text.acceptButton;
  const currentDeclineButtonText = displayedTexts?.declineButtonText || text.declineButton;
  const currentPersonalizeButtonText = displayedTexts?.personalizeButtonText || text.personalizeButton;
  const currentPersonalizeValidationText = displayedTexts?.personalizeValidationText || text.personalizeValidation;

  // Determine the effective display language (flips when alt lang is toggled)
  const isEnglishDisplay = useAltLang ? language !== 'en' : language === 'en';

  return displayCookieNotice ? (
    <div
      className={styles.backgroundWrapper}
      role="dialog"
      aria-modal="true"
      aria-label={isEnglishDisplay ? 'Cookie consent' : 'Cookie-Einwilligung'}
      ref={dialogRef}
      tabIndex={-1}
    >
      <div className={styles.background}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {displayedTexts?.children || children}
          </div>
          {altLangTexts && (
            <button
              type="button"
              onClick={() => setUseAltLang((prev) => !prev)}
              className="ml-3 flex-shrink-0 text-sm text-latori-green font-semibold border border-latori-green-dark rounded px-2 py-1 hover:bg-latori-green hover:text-black transition-colors"
              aria-label={useAltLang
                ? (language === 'en' ? 'Switch to English' : 'Auf Deutsch wechseln')
                : (language === 'en' ? 'Auf Deutsch wechseln' : 'Switch to English')}
            >
              {useAltLang ? (language === 'en' ? 'EN' : 'DE') : (altLangLabel || (language === 'en' ? 'DE' : 'EN'))}
            </button>
          )}
        </div>
        <div className={styles.linkWrapper}>
          <a
            className={styles.imprintLink}
            href={`${isEnglishDisplay ? '/en' : ''}/imprint`}
          >
            {currentImprintText}
          </a>
          <a
            className={styles.privacyLink}
            href={`${isEnglishDisplay ? '/en' : ''}/privacy`}
          >
            {currentPrivacyText}
          </a>
        </div>

        {displayCookieList && (
        <form onSubmit={validatePreferences}>
          <div className={styles.cookiesList}>
            {prop.cookiesList.map((c, idx) => {
              const altCookieTexts = displayedTexts?.cookies?.[idx];
              return (
                <div key={c.name} className={styles.cookieMap}>
                  <input
                    type="checkbox"
                    defaultChecked={c.default}
                    disabled={!c.editable}
                    value={c.name}
                    id={c.name}
                  />
                  <label htmlFor={c.name} className={styles.cookieTitle}>
                    {altCookieTexts?.title || c.title}
                  </label>
                  <p>{altCookieTexts?.text || c.text}</p>
                </div>
              );
            })}
            <div className={styles.personalizeValidationWrapper}>
              <button type="submit" className={styles.personalizeValidation}>
                {currentPersonalizeValidationText}
              </button>
            </div>
          </div>
        </form>
        )}

        {!displayCookieList && (
          <div className={styles.wrapper}>
            {prop.personalizeButton && (
              <button
                type="button"
                onClick={() => setDisplayCookieList(true)}
                className={styles.personalizeButton}
              >
                {currentPersonalizeButtonText}
              </button>
            )}
            <div className={styles.buttonWrapper}>
              {prop.declineButtonActivated && (
                <button
                  type="button"
                  onClick={declineCookie}
                  className={styles.declineBtn}
                >
                  {currentDeclineButtonText}
                </button>
              )}
              <button
                type="button"
                onClick={acceptCookie}
                className={styles.acceptBtn}
              >
                {currentAcceptButtonText}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;
}
